import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
const _excluded = ["billId"],
  _excluded2 = ["billId"],
  _excluded3 = ["companyId", "billId"],
  _excluded4 = ["companyId"],
  _excluded5 = ["companyId"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { requestService } from 'src/core/services/api';
import { API_SERVICE_NAMES } from 'src/constants/apiServiceName';
import { billAutomationBillsRoutes, billingDashboardBaseUrl } from './routes';

/** All three parts are required: the answer is one bill, and any part missing changes which. */

/**
 * A saved correction (LB-5840). `data` is the narrow edit payload built by `toBillEditPayload` —
 * deliberately not the whole extraction, so a client cannot re-target an allocation or dress a
 * typed value up as a confidently extracted one.
 *
 * It is a **whole** payload, not a patch: the service assigns every field on it unconditionally,
 * so a key left out is read as "nothing here" and erases what was there.
 */

/** One block of a portfolio bill and the property a person chose for it (LB-5910). */

/** One block of charges, as the bill prints it, and what it was matched to (LB-5910). */

/**
 * The optional body of a push. Both fields are corrections a person made to what OCR read, and the
 * service holds each to its own rules — an invoice number it cannot use is refused rather than dropped.
 */

/**
 * One page of the company's bills, filtered and sorted by the service (LB-5842).
 *
 * Everything but `companyId` is built by `toBillsRequestParams`, the only place the page's own
 * values — a status label, a filed address, a utility name — become something a database can answer.
 * Anything unset is left out entirely rather than sent empty.
 */

/**
 * A bills download (LB-6033): the bills the page names -- the selected ones, or with none selected the Processed bills
 * on the page.
 */

/** Outcome of one file in a batch: `billId` is null when it was a duplicate or failed to land. */

/** Where to PUT a bill file, and what to call the key afterwards. */

/**
 * Registers a bill whose file is already in the bucket.
 *
 * `channel` decides what the bill is and how it is timed: `Portal` is the Bills page, `Manual` is
 * apply billing. The service takes these two and no others — the rest of its source vocabulary is
 * provenance it establishes for itself.
 */

/**
 * A bill's AP push state as the dashboard list carries it. One document is one invoice and one payable,
 * so this is a single verdict rather than a count of parts done.
 */

/** One row of the billing dashboard list. Dates are date-only strings (YYYY-MM-DD). */
/**
 * One utility a bill charges for. `utilityId` and `propertyId` are what the services resolved when
 * they validated the bill; either is null while nobody has said which utility was meant.
 */

/**
 * How far one service of a bill has got towards being allocated. Anything past `allocating` is the
 * allocation service's own account of it, read live.
 */
export const BILL_ALLOCATION_STATE = {
  /** Nobody has allocated it; the card offers the suggestion. */
  notAllocated: 'NotAllocated',
  /** The allocation service refused it. `error` says why, and it can be tried again. */
  failed: 'Failed',
  /** The allocation bill exists and its table is being worked out. */
  allocating: 'Allocating',
  /**
   * The table is worked out and waiting to be approved. Allocating stops here: approving is what charges
   * residents, so it waits for somebody to press Approve (LB-6052).
   */
  awaitingApproval: 'AwaitingApproval',
  /** The bill rose sharply enough that somebody has to compare it before it can be approved. */
  needsReview: 'NeedsReview',
  /** Approved: the allocation table is the thing to show, not a suggestion. */
  allocated: 'Allocated',
  /** The table was skipped, so this service will not be charged on. */
  skipped: 'Skipped'
};

/** One service of a bill and where its allocation has got to. */

/** How a person's choice of month went (LB-6009): done, or refused in the service's words. */

/** A refusal (409) or no answer (503) carries the service's words; anything else that resolved is done. */
export const toScheduleChoiceResponse = (status, data) => {
  var _data$message;
  return status === 409 || status === 503 ? {
    isSuccessful: false,
    message: (_data$message = data === null || data === void 0 ? void 0 : data.message) !== null && _data$message !== void 0 ? _data$message : null
  } : {
    isSuccessful: true,
    message: null
  };
};

/** A bill scheduled for a later month than an Apply Billing table's (LB-6009): "A later bill is on file". */

/** One service to allocate, with the values as they stand on the card. */

/** One allocation table to approve, by the property utility it allocates: one table each (LB-6015). */

/**
 * How long the OCR pipeline takes to read a bill. Named for what it measures: the model's own time,
 * not the wait from a bill arriving to being usable. The server stamps the wider figure too, but that
 * one cannot answer for bills processed before the stamps existed, and this one can.
 */

/**
 * The Bills page tiles, over the company's whole book of bills rather than the page the table loaded.
 *
 * The counts are always answered. The two measured figures — `autoAttachedPercent` and
 * `extractionTime` — are `null` when there is nothing to measure, never `0`: a zero renders as a
 * claim, and the tiles have an "unavailable" state precisely so they need not make one. Typed that
 * way deliberately, so the unavailable branch cannot be forgotten at the call site.
 *
 * `billsSummary.js` additionally treats any count as unavailable when it is missing. That guards a
 * client deployed ahead of the service, not anything this contract permits.
 */

/** A refused delete is an answer rather than a broken request: `message` says what holds the bill. */

/**
 * What a retry of one bill may offer (LB-5787). Stages are the service's names: `currentStage` is
 * where the bill's read stopped (Haiku, Sonnet, Phase2Fix, Textract, Failed), `nextStage` where a
 * re-read of its own file would start (SonnetVision, Textract, EscalatedVision) — null at the top.
 */

/** Re-read the bill's own file, or replace it with a file already uploaded through `upload-url`. */

/**
 * One reason a bill stopped where it did -- failed, held, waiting for a property, a duplicate -- in the
 * service's customer wording (LB-5918), with what to do next.
 */

/** Status envelope returned by POST /bills and GET /bills/{billId}. */

/**
 * One field that moved, as the trail records it (LB-5840). Values arrive canonical — ISO dates,
 * invariant decimals — and are formatted where they are rendered, so the trail is not frozen into
 * whichever locale wrote it.
 */

/** One recorded change to a bill. */

/**
 * What a save or a discard produced. `entry` is null when the save changed nothing, which the
 * service answers 200 to rather than 409 — saving what the bill already says is not a conflict.
 * The version comes back either way, so the next attempt always has something current to send.
 */

/**
 * Why a save or a discard was refused, as the service names it. `Held` and `NotSent` are a hand entry (LB-6095)
 * that was saved all the same: held for staff on its account, or not sent on to be checked.
 */

/** A save's or a discard's 409, read as an answer. */
const toEditRefusal = data => {
  var _body$message, _body$outcome, _body$copyBillId;
  const body = data;
  return {
    isSuccessful: false,
    message: (_body$message = body === null || body === void 0 ? void 0 : body.message) !== null && _body$message !== void 0 ? _body$message : null,
    outcome: (_body$outcome = body === null || body === void 0 ? void 0 : body.outcome) !== null && _body$outcome !== void 0 ? _body$outcome : null,
    copyBillId: (_body$copyBillId = body === null || body === void 0 ? void 0 : body.copyBillId) !== null && _body$copyBillId !== void 0 ? _body$copyBillId : null
  };
};

/** `response` is null when nothing has been uploaded for that cycle, property and utility yet. */

/** Why the bill could not be pushed (no AppFolio vendor, no GL code, unmapped property...). */

/** Present when the same invoice already reached AppFolio, through this bill or another one. */

/** A property covered by one attempt, with the utilities and the share of the bill it carries. */

/**
 * One AP push attempt. A document is one invoice and one payable, so a trigger records exactly one
 * attempt; the extra rows a status read carries are that attempt's history.
 */

/** Push state of one bill, rolled up over its attempts. */

/**
 * An upload naming a property the person may not put a bill on is refused with 400, and one whose
 * property could not be checked with 503 (LB-5910) -- both carrying the service's own wording. They are
 * read as answers, the way attach reads its 409, so the person sees that wording rather than a generic
 * failure (the async layer shows a bare `message` only to Livable staff).
 */
const acceptsUploadRefusal = status => status === 400 || status === 503 || status >= 200 && status < 300;
const asUploadAnswer = resp => {
  var _message, _resp$data;
  return resp.status === 400 || resp.status === 503 ? {
    isSuccessful: false,
    message: (_message = (_resp$data = resp.data) === null || _resp$data === void 0 ? void 0 : _resp$data.message) !== null && _message !== void 0 ? _message : null
  } : resp.data;
};
export class BillAutomationBills {
  constructor() {
    _defineProperty(this, "routes", billAutomationBillsRoutes);
  }
  async startBill(data, options) {
    const formData = new FormData();
    formData.append('PropertyId', data.propertyId);
    formData.append('File', data.file);
    const appendList = (key, value) => {
      if (!value) return;
      const list = Array.isArray(value) ? value : [value];
      list.filter(Boolean).forEach(item => formData.append(key, item));
    };
    appendList('UtilityNames', data.utilityNames);
    if (data.propertyAddress) formData.append('PropertyAddress', data.propertyAddress);
    appendList('RequiredUtilities', data.requiredUtilities);
    if (data.billingCycleId) formData.append('BillingCycleId', data.billingCycleId);
    if (data.utilityId) formData.append('UtilityId', data.utilityId);
    const resp = await requestService(API_SERVICE_NAMES.BILL_AUTOMATION).post(this.routes.baseUrl, formData, _objectSpread({
      validateStatus: acceptsUploadRefusal
    }, options.cancelToken));
    return asUploadAnswer(resp);
  }
  async getBill(billId, options) {
    const resp = await requestService(API_SERVICE_NAMES.BILL_AUTOMATION).get(this.routes.billById(billId), _objectSpread({}, options.cancelToken));
    return resp.data;
  }

  /**
   * The bill somebody uploaded for this cycle, property and utility, whatever state it is in — how
   * apply billing picks up the document it was working on without having kept its id. Resolves with
   * a null `response` when there is none, which is the ordinary answer, not a failure.
   */
  async getBillForCycle(data, options) {
    const resp = await requestService(API_SERVICE_NAMES.BILL_AUTOMATION).get(this.routes.billForCycle, _objectSpread({
      params: {
        billingCycleId: data.billingCycleId,
        propertyId: data.propertyId,
        utilityId: data.utilityId
      }
    }, options.cancelToken));
    return resp.data;
  }

  /**
   * Asks where to put a bill file.
   *
   * The service refuses a content type it does not take before issuing anything, so a failure here
   * usually means the file was never going to be accepted rather than that an upload went wrong.
   * Not worth branching on the status to say so: that refusal currently answers 500, because the
   * service raises it as an ArgumentException and the shared middleware maps only its own
   * ValidationException to 400. `billContentTypeOf` is what keeps that case away from the network.
   */
  async createBillUploadUrl(data, options) {
    const resp = await requestService(API_SERVICE_NAMES.BILL_AUTOMATION).post(this.routes.billUploadUrl, data, _objectSpread({}, options.cancelToken));
    return resp.data;
  }

  /**
   * Sends the file to storage, straight from the browser.
   *
   * On the anonymous client deliberately: the presigned URL IS the authorization, and an
   * Authorization header alongside it makes S3 reject the signature. The Content-Type has to be the
   * one the URL was issued for, because it is signed into it — anything else is refused too.
   */
  async putBillFileToStorage(data, options) {
    const onUploadProgress = data.onUploadProgress;
    await requestService(API_SERVICE_NAMES.ANONYMOUS).put(data.uploadUrl, data.file, _objectSpread(_objectSpread({
      headers: {
        'Content-Type': data.contentType
      }
    }, onUploadProgress ? {
      onUploadProgress: event => {
        var _event$loaded;
        return onUploadProgress((_event$loaded = event.loaded) !== null && _event$loaded !== void 0 ? _event$loaded : 0);
      }
    } : {}), options.cancelToken));
  }

  /** Registers an uploaded file as a bill and starts its extraction. */
  async startBillFromStorage(data, options) {
    const resp = await requestService(API_SERVICE_NAMES.BILL_AUTOMATION).post(this.routes.billFromStorage, data, _objectSpread({
      validateStatus: acceptsUploadRefusal
    }, options.cancelToken));
    return asUploadAnswer(resp);
  }

  /**
   * Says an upload has no more files coming (LB-5862).
   *
   * Best-effort by design, and the caller does not wait on it: an upload nobody closes is closed by
   * the service's own sweep, which delays its summary rather than losing it. So a failure here is
   * not worth telling the person about — they uploaded their files and their files went up.
   */
  async sealBillsUpload(data, options) {
    await requestService(API_SERVICE_NAMES.BILL_AUTOMATION).post(this.routes.companyBillsBatchSeal(data.companyId), {
      uploadKey: data.uploadKey
    }, _objectSpread({
      baseURL: billingDashboardBaseUrl
    }, options.cancelToken));
  }

  /** Where every service on the bill has got to, without changing anything. */
  async getBillAllocations(data, options) {
    const resp = await requestService(API_SERVICE_NAMES.BILL_AUTOMATION).get(this.routes.billAllocations(data.companyId, data.billId), _objectSpread({
      baseURL: billingDashboardBaseUrl
    }, options.cancelToken));
    return resp.data;
  }

  /**
   * A short-lived presigned URL for the bill's stored source document (LB-5724 PR A). 404 when the
   * bill has no stored document — the caller shows a "no document" state. The URL is ephemeral: open
   * it in an iframe / new tab; never fetch()/XHR it (the bill bucket has no CORS) and never cache it.
   */
  async getBillFileUrl(data, options) {
    const resp = await requestService(API_SERVICE_NAMES.BILL_AUTOMATION).get(this.routes.companyBillFileUrl(data.companyId, data.billId), _objectSpread({
      baseURL: billingDashboardBaseUrl
    }, options.cancelToken));
    return resp.data;
  }

  /**
   * Creates the allocation bill for each service named, and nothing more: the table is worked out
   * after the call returns and stops at "Awaiting approval". Safe to repeat — a service already
   * allocated is left alone.
   *
   * `approveWhenReady: false` is what keeps it there (LB-6052). Left out, the service also approves
   * every table that has finished calculating, which charges residents, and that is the step this page
   * asks for on its own with `approveBill`. A service from before LB-6052 ignores the field and still
   * approves, so it has to be deployed first.
   *
   * `confirmedOldServicePeriod` is sent only when the person confirmed the warning that a service period on the bill is
   * long past (LB-6030); property-service then records who confirmed it and when.
   */
  async allocateBill(data, options) {
    var _data$services;
    const resp = await requestService(API_SERVICE_NAMES.BILL_AUTOMATION).post(this.routes.allocateBill(data.companyId, data.billId), _objectSpread({
      services: (_data$services = data.services) !== null && _data$services !== void 0 ? _data$services : [],
      approveWhenReady: false
    }, data.confirmedOldServicePeriod ? {
      confirmedOldServicePeriod: true
    } : {}), _objectSpread({
      baseURL: billingDashboardBaseUrl
    }, options.cancelToken));
    return resp.data;
  }

  /**
   * "Use it this cycle" (LB-6009): every service of the bill, or the ones named, is filed under the month in
   * progress, so Apply Billing offers it there and Allocate takes it now. Refused (409) for a service already
   * allocated; 503 when billing couldn't say which cycle that is.
   */
  async useBillThisCycle(data, options) {
    var _data$services2;
    const resp = await requestService(API_SERVICE_NAMES.BILL_AUTOMATION).post(this.routes.useBillThisCycle(data.companyId, data.billId), {
      services: (_data$services2 = data.services) !== null && _data$services2 !== void 0 ? _data$services2 : []
    }, _objectSpread({
      baseURL: billingDashboardBaseUrl,
      validateStatus: status => status === 409 || status === 503 || status >= 200 && status < 300
    }, options.cancelToken));
    return toScheduleChoiceResponse(resp.status, resp.data);
  }

  /**
   * "Not for this table" (LB-6009): takes a bill the Apply Billing table brought in, but isn't saved on, off that
   * table. Kept for next month, or left on the Bills page for no table. Refused (409) once allocated there.
   */
  async setBillNotForTable(data, options) {
    const resp = await requestService(API_SERVICE_NAMES.BILL_AUTOMATION).post(this.routes.billNotForTable(data.billId), {
      propertyId: data.propertyId,
      utilityId: data.utilityId,
      billingCycleId: data.billingCycleId,
      keepForNextMonth: data.keepForNextMonth
    }, _objectSpread({
      validateStatus: status => status === 409 || status === 503 || status >= 200 && status < 300
    }, options.cancelToken));
    return toScheduleChoiceResponse(resp.status, resp.data);
  }

  /** Bills for a table's property and utility scheduled for a later month (LB-6009): "A later bill is on file". */
  async getScheduledBillsForTable(data, options) {
    const resp = await requestService(API_SERVICE_NAMES.BILL_AUTOMATION).get(this.routes.scheduledForTable, _objectSpread({
      params: {
        billingCycleId: data.billingCycleId,
        propertyId: data.propertyId,
        utilityId: data.utilityId
      }
    }, options.cancelToken));
    return resp.data;
  }

  /**
   * Approves the named tables (LB-6052): the step that charges the residents on them, and for an
   * AppFolio company sends the charges on. The service approves only a table that is ready — it has
   * charges, is not held for review and is not approved already — and answers with where every
   * service on the bill now stands, as `getBillAllocations` does.
   */
  async approveBill(data, options) {
    const resp = await requestService(API_SERVICE_NAMES.BILL_AUTOMATION).post(this.routes.approveBill(data.companyId, data.billId), {
      tables: data.tables
    }, _objectSpread({
      baseURL: billingDashboardBaseUrl
    }, options.cancelToken));
    return resp.data;
  }

  /**
   * What the attach dialog offers (LB-5910): the bill's blocks of charges as validation saw them, and
   * the property each was matched to — so a portfolio bill is attached block by block and the person is
   * asked only about the blocks that did not match.
   */
  async getBillAttachOptions(data, options) {
    const resp = await requestService(API_SERVICE_NAMES.BILL_AUTOMATION).get(this.routes.attachOptions(data.billId), _objectSpread({}, options.cancelToken));
    return resp.data;
  }

  /**
   * Deletes the bill for good. The service refuses a bill any of whose services has been allocated
   * and answers 409 with the reason, which is read here rather than thrown: nothing is broken, the
   * bill is simply not the caller's to delete any more.
   */
  /**
   * Approve-on-attach (LB-5766): attach a Rejected or NeedsProperty bill to a property and, in the
   * same gesture, allow its sender (or their whole domain) and save its account number — so the
   * next bill from this sender attaches itself. 409 carries the service's refusal (wrong status,
   * concurrent change) as an answer, not an error.
   */
  async attachBill(data, options) {
    var _message2, _resp$data2;
    const billId = data.billId,
      body = _objectWithoutProperties(data, _excluded);
    const resp = await requestService(API_SERVICE_NAMES.BILL_AUTOMATION).post(this.routes.attachBill(billId), body, _objectSpread({
      validateStatus: status => status === 409 || status >= 200 && status < 300
    }, options.cancelToken));
    return resp.status === 409 ? {
      isSuccessful: false,
      message: (_message2 = (_resp$data2 = resp.data) === null || _resp$data2 === void 0 ? void 0 : _resp$data2.message) !== null && _message2 !== void 0 ? _message2 : null
    } : resp.data;
  }

  /**
   * Save a correction to a bill (LB-5840). 409 carries the service's refusal — the bill was
   * allocated or pushed while the drawer was open, somebody else got there first, or the status
   * moved — and is read here as an answer rather than thrown, the same way attach and delete do:
   * nothing is broken, the bill is simply not in a state to take the edit.
   */
  async saveBillEdit(data, options) {
    const billId = data.billId,
      body = _objectWithoutProperties(data, _excluded2);
    const resp = await requestService(API_SERVICE_NAMES.BILL_AUTOMATION).put(this.routes.billEdit(billId), body, _objectSpread({
      validateStatus: status => status === 409 || status >= 200 && status < 300
    }, options.cancelToken));
    return resp.status === 409 ? toEditRefusal(resp.data) : resp.data;
  }

  /**
   * Throw every edit away and return the bill to what was extracted. The version goes as a query
   * param so a discard is refused for the same reason a save is when somebody else moved first.
   */
  async discardBillEdit(data, options) {
    const resp = await requestService(API_SERVICE_NAMES.BILL_AUTOMATION).delete(this.routes.billEdit(data.billId), undefined, _objectSpread({
      params: {
        version: data.version
      },
      validateStatus: status => status === 409 || status >= 200 && status < 300
    }, options.cancelToken));
    return resp.status === 409 ? toEditRefusal(resp.data) : resp.data;
  }

  /**
   * Who changed what, newest first. `take` and `skip` page it; `total` says how many there are in
   * all, so the panel can offer "show all N" while holding only the latest.
   */
  async getBillEditHistory(data, options) {
    const resp = await requestService(API_SERVICE_NAMES.BILL_AUTOMATION).get(this.routes.billEditHistory(data.billId), _objectSpread({
      params: {
        skip: data.skip,
        take: data.take
      }
    }, options.cancelToken));
    return resp.data;
  }
  async deleteBill(data, options) {
    var _resp$data$message, _resp$data3;
    const resp = await requestService(API_SERVICE_NAMES.BILL_AUTOMATION).delete(this.routes.companyBill(data.companyId, data.billId), undefined, _objectSpread({
      baseURL: billingDashboardBaseUrl,
      validateStatus: status => status === 409 || status >= 200 && status < 300
    }, options.cancelToken));
    return resp.status === 409 ? {
      isSuccessful: false,
      message: (_resp$data$message = (_resp$data3 = resp.data) === null || _resp$data3 === void 0 ? void 0 : _resp$data3.message) !== null && _resp$data$message !== void 0 ? _resp$data$message : null
    } : {
      isSuccessful: true,
      message: null
    };
  }
  async getBillRetryOptions(data, options) {
    const resp = await requestService(API_SERVICE_NAMES.BILL_AUTOMATION).get(this.routes.companyBillRetryOptions(data.companyId, data.billId), _objectSpread({
      baseURL: billingDashboardBaseUrl
    }, options.cancelToken));
    return resp.data;
  }

  /**
   * Retry a bill (LB-5787): it is archived and a new bill takes its place. 409 carries the service's
   * refusal — allocated or pushed, its file being read right now, every stage already tried — and is
   * read here as an answer rather than thrown, the same way attach and delete do.
   */
  async retryBill(data, options) {
    var _message3, _resp$data4;
    const companyId = data.companyId,
      billId = data.billId,
      body = _objectWithoutProperties(data, _excluded3);
    const resp = await requestService(API_SERVICE_NAMES.BILL_AUTOMATION).post(this.routes.companyBillRetry(companyId, billId), body, _objectSpread({
      baseURL: billingDashboardBaseUrl,
      validateStatus: status => status === 409 || status >= 200 && status < 300
    }, options.cancelToken));
    return resp.status === 409 ? {
      isSuccessful: false,
      message: (_message3 = (_resp$data4 = resp.data) === null || _resp$data4 === void 0 ? void 0 : _resp$data4.message) !== null && _message3 !== void 0 ? _message3 : null
    } : resp.data;
  }
  async getCompanyBills(data, options) {
    const companyId = data.companyId,
      params = _objectWithoutProperties(data, _excluded4);
    const resp = await requestService(API_SERVICE_NAMES.BILL_AUTOMATION).get(this.routes.companyBills(companyId), _objectSpread({
      baseURL: billingDashboardBaseUrl,
      params,
      // Repeated keys — `statuses=a&statuses=b` — not `statuses[]=` or `statuses[0]=`. The service
      // binds a list from the first and from neither of the others, so axios's bracketed default
      // would leave every multi-select filter binding as nothing: a filter that silently stops
      // narrowing rather than one that fails.
      paramsSerializer: {
        indexes: null
      }
    }, options.cancelToken));
    return resp.data;
  }

  /** What a bills download would hold, and whether it would come by email, before it starts (LB-6033). */
  async previewBillsExport(data, options) {
    return this.postBillsExport(this.routes.companyBillsExportPreview(data.companyId), data, options);
  }

  /** Prepares a bills download: a link straight away, or an export emailed when it is ready (LB-6033). */
  async createBillsExport(data, options) {
    return this.postBillsExport(this.routes.companyBillsExports(data.companyId), data, options);
  }

  /** A download emailed to the person who asked for it, with a fresh link once it is ready (LB-6033). */
  async getBillsExport(data, options) {
    const resp = await requestService(API_SERVICE_NAMES.BILL_AUTOMATION).get(this.routes.companyBillsExport(data.companyId, data.exportId), _objectSpread({
      baseURL: billingDashboardBaseUrl
    }, options.cancelToken));
    return resp.data;
  }
  async postBillsExport(route, data, options) {
    const companyId = data.companyId,
      body = _objectWithoutProperties(data, _excluded5);
    const resp = await requestService(API_SERVICE_NAMES.BILL_AUTOMATION).post(route, body, _objectSpread({
      baseURL: billingDashboardBaseUrl
    }, options.cancelToken));
    return resp.data;
  }

  /**
   * What the Bills page can offer in its filter dropdowns (LB-5842). Describes the company's whole
   * book rather than the page the table holds, and is narrowed to what this caller may see.
   */
  async getCompanyBillFilterOptions(data, options) {
    const resp = await requestService(API_SERVICE_NAMES.BILL_AUTOMATION).get(this.routes.companyBillFilterOptions(data.companyId), _objectSpread({
      baseURL: billingDashboardBaseUrl
    }, options.cancelToken));
    return resp.data;
  }
  async getCompanyBillsSummary(data, options) {
    const resp = await requestService(API_SERVICE_NAMES.BILL_AUTOMATION).get(this.routes.companyBillsSummary(data.companyId), _objectSpread({
      baseURL: billingDashboardBaseUrl
    }, options.cancelToken));
    return resp.data;
  }

  /**
   * Pushes an uploaded bill to AppFolio Accounts Payable, without waiting for an allocation. Accepted (202)
   * only means the request was taken: check each attempt's `dispatched` flag, and its `blockers` /
   * `alreadyPushed` when it is false. 422 when the bill as a whole cannot be pushed.
   */
  /**
   * Both body fields stand in for something a person supplied that the document's own extraction could
   * not: `overrideTotalAmount` when they edited the Bill total by hand, `invoiceNumber` when the bill
   * carries none, or carries one the extraction was not confident about.
   *
   * Each is dropped unless it has a value, and a push with neither sends no body at all — the ordinary
   * case, and the one every caller took before these existed.
   */
  async pushBillToAppfolio(billId, body, options) {
    const supplied = Object.fromEntries(Object.entries(body !== null && body !== void 0 ? body : {}).filter(_ref => {
      let _ref2 = _slicedToArray(_ref, 2),
        value = _ref2[1];
      return value !== undefined && value !== null && value !== '';
    }));
    const resp = await requestService(API_SERVICE_NAMES.BILL_AUTOMATION).post(this.routes.appfolioPush(billId), Object.keys(supplied).length ? supplied : undefined, _objectSpread({}, options.cancelToken));
    return resp.data;
  }

  /** AppFolio AP push status for a batch of uploaded bills, one entry per requested bill id. */
  async getAppfolioPushStatus(data, options) {
    const resp = await requestService(API_SERVICE_NAMES.BILL_AUTOMATION).post(this.routes.appfolioPushStatus, {
      billIds: data.billIds
    }, _objectSpread({}, options.cancelToken));
    return resp.data;
  }
}